import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Loader2, Search, CalendarDays, Users, Clock, Check } from "lucide-react";
import { toast } from "sonner";

// ─── helpers ────────────────────────────────────────────────────────────────
function fmtISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtFecha(d) {
    const s = d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function hhmm(h, m) { return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; }
function toMin(s) { const [h, m] = s.split(":"); return parseInt(h) * 60 + parseInt(m); }
function fromMin(min) { return hhmm(Math.floor(min / 60), min % 60); }

const DURACIONES = [
    { v: 30, l: "30 min" },
    { v: 60, l: "1 h" },
    { v: 90, l: "1 h 30" },
    { v: 120, l: "2 h" },
    { v: 180, l: "3 h" },
];

const CAPACIDADES = [
    { v: "", l: "Cualquier capacidad" },
    { v: "1", l: "1 persona" },
    { v: "2", l: "2 personas" },
    { v: "3", l: "3 personas" },
    { v: "4", l: "4 personas" },
    { v: "5", l: "5 personas" },
    { v: "6", l: "6 personas" },
    { v: "7", l: "7 personas" },
    { v: "8", l: "8 personas" },
    { v: "9", l: "9 personas" },
    { v: "10", l: "10 personas" },
    { v: "11", l: "11 personas" },
    { v: "12", l: "12 personas" },
    { v: "13", l: "Más de 12" },
];

const HORAS = (() => {
    const out = [];
    for (let m = 7 * 60; m <= 22 * 60 + 30; m += 30) out.push(fromMin(m));
    return out;
})();

/**
 * Búsqueda de disponibilidad para reservar una sala.
 *
 * Props:
 * - unidades: lista opcional de unidades para mostrar el picker "Reservar para".
 *             Si se omite o queda vacía, se asume usuario_edificio (sin picker).
 * - unidadId: unidad seleccionada (controlada desde afuera) cuando hay picker.
 * - onChangeUnidad: callback para cambiar la unidad seleccionada.
 * - redirectOnSuccess: ruta a navegar tras reservar OK ("/inicio" para usuario, undefined para no navegar).
 * - onReservaCreada: callback opcional luego de reservar OK (para refrescar la grilla del padre).
 */
export default function BuscarDisponibilidad({
    unidades = null,
    unidadId = "",
    onChangeUnidad = () => {},
    redirectOnSuccess = null,
    onReservaCreada = null,
}) {
    const navigate = useNavigate();
    const needsUnidad = Array.isArray(unidades);

    const [day, setDay] = useState(() => { const x = new Date(); x.setHours(0, 0, 0, 0); return x; });
    const [hora, setHora] = useState(() => {
        const n = new Date();
        const m = (n.getHours() * 60 + n.getMinutes() + 30);
        return fromMin(Math.min(Math.max(Math.ceil(m / 30) * 30, 7 * 60), 22 * 60));
    });
    const [duracion, setDuracion] = useState(60);
    const [capMin, setCapMin] = useState("");
    const [calOpen, setCalOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [reservandoSala, setReservandoSala] = useState(null);

    const today0 = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
    const isToday = day.getTime() === today0.getTime();
    const isPastDay = day.getTime() < today0.getTime();

    useEffect(() => { if (isPastDay) setDay(today0); }, [isPastDay, today0]);

    const fecha = fmtISODate(day);
    const fin = fromMin(toMin(hora) + duracion);

    const buscar = useCallback(async () => {
        setLoading(true);
        setData(null);
        try {
            const params = { fecha, hora_inicio: hora, duracion_min: duracion };
            const cap = parseInt(capMin, 10);
            if (cap > 0) params.capacidad_minima = cap;
            const r = await api.get("/reservas/disponibilidad", { params });
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [fecha, hora, duracion, capMin]);

    useEffect(() => {
        const t = setTimeout(buscar, 250);
        return () => clearTimeout(t);
    }, [buscar]);

    const reservar = async (sala, ini, finStr) => {
        if (reservandoSala) return;
        if (needsUnidad && !unidadId) {
            toast.error("Elegí la unidad para la cual reservás");
            return;
        }
        setReservandoSala(sala.sala_id);
        try {
            const start = toMin(ini);
            const total = toMin(finStr) - start;
            const slots = total / 30;
            const results = await Promise.allSettled(
                Array.from({ length: slots }, (_, i) => {
                    const a = start + i * 30;
                    const b = a + 30;
                    const payload = {
                        sala_id: sala.sala_id,
                        fecha,
                        hora_inicio: fromMin(a),
                        hora_fin: fromMin(b),
                        asistentes: 1,
                        motivo: "",
                    };
                    if (needsUnidad && unidadId) payload.unidad_id = unidadId;
                    return api.post("/reservas", payload);
                })
            );
            const fail = results.filter((r) => r.status === "rejected");
            if (fail.length === 0) {
                toast.success(`Reservado ${ini}–${finStr} en ${sala.nombre}`);
                if (onReservaCreada) await onReservaCreada();
                if (redirectOnSuccess) navigate(redirectOnSuccess);
                else await buscar();
                return;
            }
            const firstErr = fail[0];
            toast.error(formatApiError(firstErr.reason));
            await buscar();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReservandoSala(null);
        }
    };

    return (
        <div className="space-y-3">
            <Card className="border-primary/20 bg-primary-soft/30">
                <CardContent className="p-3 space-y-3">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">¿Cuándo necesitás la sala?</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, -1))} disabled={isToday} data-testid="buscar-day-prev">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Popover open={calOpen} onOpenChange={setCalOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="flex-1 px-3 py-2 rounded-md border border-border bg-card hover:bg-primary-soft transition-colors text-sm font-heading font-bold text-center"
                                    data-testid="buscar-day-current"
                                >
                                    {fmtFecha(day)}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="center">
                                <Calendar
                                    mode="single"
                                    selected={day}
                                    onSelect={(d) => {
                                        if (!d) return;
                                        const x = new Date(d); x.setHours(0, 0, 0, 0);
                                        if (x.getTime() < today0.getTime()) return;
                                        setDay(x);
                                        setCalOpen(false);
                                    }}
                                    disabled={(d) => {
                                        const x = new Date(d); x.setHours(0, 0, 0, 0);
                                        return x.getTime() < today0.getTime();
                                    }}
                                    initialFocus
                                    locale={undefined}
                                    weekStartsOn={1}
                                />
                            </PopoverContent>
                        </Popover>
                        <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, 1))} data-testid="buscar-day-next">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[11px] flex items-center gap-1 mb-1"><Clock className="h-3 w-3" /> Hora de inicio</Label>
                            <Select value={hora} onValueChange={setHora}>
                                <SelectTrigger className="h-9" data-testid="buscar-hora"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {HORAS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[11px] flex items-center gap-1 mb-1"><Users className="h-3 w-3" /> Cantidad de personas</Label>
                            <Select value={capMin || "__any__"} onValueChange={(v) => setCapMin(v === "__any__" ? "" : v)}>
                                <SelectTrigger className="h-9" data-testid="buscar-capacidad"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {CAPACIDADES.map((c) => (
                                        <SelectItem key={c.l} value={c.v || "__any__"}>{c.l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-[11px] mb-1 block">Duración</Label>
                        <div className="flex flex-wrap gap-1.5">
                            {DURACIONES.map((d) => (
                                <button
                                    key={d.v}
                                    type="button"
                                    onClick={() => setDuracion(d.v)}
                                    data-testid={`buscar-dur-${d.v}`}
                                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${duracion === d.v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-primary-soft"}`}
                                >
                                    {d.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {needsUnidad && (
                        <div>
                            <Label className="text-[11px] mb-1 block">Reservar para</Label>
                            <Select value={unidadId || ""} onValueChange={onChangeUnidad}>
                                <SelectTrigger className="h-9" data-testid="buscar-unidad"><SelectValue placeholder="Elegí unidad" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {unidades.map((u) => (
                                        <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.etiqueta}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <p className="text-[11px] text-muted-foreground font-mono">
                        Buscando: <span className="text-foreground">{hora} – {fin}</span>
                        {capMin && <span> · capacidad ≥ {capMin}</span>}
                    </p>
                </CardContent>
            </Card>

            {loading && (
                <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Buscando salas…
                </CardContent></Card>
            )}

            {!loading && data && data.total > 0 && (
                <div className="space-y-1.5">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2">
                        <Search className="h-3 w-3" /> {data.total} sala{data.total === 1 ? "" : "s"} disponible{data.total === 1 ? "" : "s"}
                    </p>
                    {data.salas.map((s) => (
                        <Card key={s.sala_id} className="hover:bg-primary-soft/30 transition-colors">
                            <CardContent className="p-3 flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{s.nombre}</p>
                                    <p className="text-[11px] text-muted-foreground font-mono">
                                        Capacidad {s.capacidad} · {s.hora_inicio} a {s.hora_fin}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => reservar(s, s.hora_inicio, s.hora_fin)}
                                    disabled={!!reservandoSala || (needsUnidad && !unidadId)}
                                    data-testid={`reservar-${s.sala_id}`}
                                    className="shrink-0"
                                >
                                    {reservandoSala === s.sala_id
                                        ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Reservando…</>
                                        : <><Check className="h-3.5 w-3.5 mr-1" />Reservar</>}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {!loading && data && data.total === 0 && (
                <Card className="border-dashed">
                    <CardContent className="p-5 text-center space-y-3">
                        <p className="text-sm font-medium">No hay salas disponibles de {data.hora_inicio} a {data.hora_fin}.</p>
                        {data.alternativas.length > 0 ? (
                            <>
                                <p className="text-[12px] text-muted-foreground">Horarios cercanos con disponibilidad:</p>
                                <div className="flex flex-col gap-1.5 items-stretch max-w-sm mx-auto">
                                    {data.alternativas.map((a, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setHora(a.hora_inicio)}
                                            data-testid={`buscar-alt-${i}`}
                                            className="text-left px-3 py-2 rounded-md border border-border bg-background hover:bg-primary-soft transition-colors flex items-center justify-between gap-2"
                                        >
                                            <span className="font-mono text-sm">{a.hora_inicio} a {a.hora_fin}</span>
                                            <span className="text-[11px] text-success font-medium">
                                                {a.salas_count} sala{a.salas_count === 1 ? "" : "s"}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="text-[12px] text-muted-foreground">Probá con otro día o reducí la capacidad mínima.</p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
